import { useMemo, useState } from 'react';
import styled from 'styled-components';
import Loader from 'components/common/loader/Loader';
import { CardWrap } from '../../CardWrap';
import { MetricCardHeader } from './MetricCardHeader/MetricCardHeader';
import { MetricCardRow } from './MetricCardRow/MetricCardRow';
import { dashboardAPI } from 'services';

import {
  MOCK_DATA_APPLICATIONS_PUBLISHER,
  MOCK_DATA_CAMPAIGNS_AND_EVENTS,
  MOCK_DATA_TOP_EVENTS,
} from '../MOCK_DATA';

const METRICS = ['installs', 'cost'];
const METRICS_PUBLISHER = ['revenue', 'eCPM'];

// TYPES:
// 'Applications'
// 'ApplicationsPublisher'
// 'Campaigns'
// 'Events/Installs Ratio'
// 'Top Events'

function getMockData(type) {
  // if (type === 'Applications') return MOCK_DATA_APPLICATIONS;
  // if (type === 'Campaigns') return MOCK_DATA_CAMPAIGNS_AND_EVENTS;
  if (type === 'ApplicationsPublisher') return MOCK_DATA_APPLICATIONS_PUBLISHER;
  if (type === 'Events/Installs Ratio') return MOCK_DATA_CAMPAIGNS_AND_EVENTS;
  if (type === 'Top Events') return MOCK_DATA_TOP_EVENTS;
  return [];
}

function getRows(type, data) {
  if (type === 'Campaigns' && Array.isArray(data)) return data;
  if (type === 'Campaigns') return data.campaigns_data || [];
  if (type === 'Events/Installs Ratio') return data.campaigns_data || [];
  return data;
}

function getSelectedMetric(type) {
  if (type === 'Top Events') return 'value';
  if (type === 'ApplicationsPublisher') return METRICS_PUBLISHER[0];
  return METRICS[0];
}

function getRequestType(type) {
  if (type === 'Applications') return 'useGetApplicationsBlockQuery';
  // TO-DO: switch to useGetPublisherApplicationsBlockQuery when the API is ready.
  if (type === 'Campaigns') return 'useGetCampaignsBlockQuery';

  return null;
}

function getMetrics(type) {
  if (type === 'ApplicationsPublisher') return METRICS_PUBLISHER;
  return METRICS;
}

export const MetricCard = ({ type, numCols, timePeriod }) => {
  const [selectedMetric, setSelectedMetric] = useState(() => getSelectedMetric(type));
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedEventInd, setSelectedEventInd] = useState(0);
  const [sortDirection, setSortDirection] = useState('desc');

  const requestType = getRequestType(type);
  const requestHook = dashboardAPI[requestType] || dashboardAPI.useGetApplicationsBlockQuery;
  const metrics = getMetrics(type);

  const { data: requestData, isFetching } = requestHook(timePeriod, {
    skip: !requestType || !timePeriod?.from || !timePeriod?.to,
  });

  // TO-DO remove mock data
  const data = requestType ? requestData || [] : getMockData(type);
  const rows = useMemo(() => getRows(type, data), [type, timePeriod, requestData]);

  if (type === 'Events/Installs Ratio') console.log(data);

  const countries = useMemo(() => {
    if (type !== 'ApplicationsPublisher') return null;

    const countryMap = new Map();

    rows.forEach((item) => {
      item.country_codes?.forEach((code, ind) => {
        if (!code || countryMap.has(code)) return;

        countryMap.set(code, {
          code,
          name: item.countries?.[ind] || code,
        });
      });
    });

    return Array.from(countryMap.values());
  }, [rows, type]);

  const filteredRows = useMemo(() => {
    if (type !== 'ApplicationsPublisher' || !selectedCountry) return rows;

    return rows.filter((item) => item.country_codes?.includes(selectedCountry));
  }, [rows, selectedCountry, type]);

  const totalRevenue = useMemo(() => {
    if (type !== 'ApplicationsPublisher') return 0;

    return filteredRows.reduce((sum, item) => sum + (Number(item.revenue) || 0), 0);
  }, [filteredRows, type]);

  const events = useMemo(() => {
    if (type === 'Events/Installs Ratio') return data.event_titles;
    return [];
  }, [data, type]);

  const sortedRows = useMemo(() => {
    const rowsToSort =
      type === 'Events/Installs Ratio'
        ? filteredRows.map((item) => {
            const event = item.event_values?.[selectedEventInd];
            const eventPercentValue =
              item[selectedMetric] && event ? (event / item[selectedMetric]) * 100 : 0;

            return {
              ...item,
              event,
              eventPercentValue,
            };
          })
        : filteredRows;

    return [...rowsToSort].sort((a, b) => {
      const firstValue =
        type === 'Events/Installs Ratio'
          ? Number(a.eventPercentValue) || 0
          : Number(a[selectedMetric]) || 0;
      const secondValue =
        type === 'Events/Installs Ratio'
          ? Number(b.eventPercentValue) || 0
          : Number(b[selectedMetric]) || 0;

      return sortDirection === 'asc' ? firstValue - secondValue : secondValue - firstValue;
    });
  }, [filteredRows, selectedEventInd, selectedMetric, sortDirection, type]);

  return (
    <CardWrap numCols={numCols} style={{ minHeight: '100%' }}>
      <MetricCardHeader
        type={type}
        metrics={metrics}
        events={events}
        isTextIcons={type === 'ApplicationsPublisher'}
        countries={countries}
        selectedCountry={selectedCountry}
        selectedMetric={selectedMetric}
        selectedEventInd={selectedEventInd}
        sortDirection={sortDirection}
        onMetricChange={setSelectedMetric}
        onCountryChange={setSelectedCountry}
        onEventChange={setSelectedEventInd}
        onSortChange={setSortDirection}
      />
      <RowsWrap>
        {isFetching ? (
          <Loader.Local delay={0} />
        ) : (
          sortedRows.map((item) => (
            <MetricCardRow
              type={type}
              key={item.id}
              data={item}
              metric={selectedMetric}
              eventPercentValue={item.eventPercentValue}
              showProgressBar={type === 'ApplicationsPublisher' && selectedMetric === 'revenue'}
              progressValue={totalRevenue ? (Number(item.revenue) || 0) / totalRevenue : 0}
            />
          ))
        )}
      </RowsWrap>
    </CardWrap>
  );
};

const RowsWrap = styled.div`
  margin-top: 24px;
`;
